import Styles from "@/assets/styles/Styles";
import { useStore } from "@/Store";
import { Close, SmartToy, ArrowRightAlt } from "@mui/icons-material";
import { Button, styled } from "@mui/material";
import { type FC, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const fadedValue = "0.4";

const TopRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;

  h2 {
    display: flex;
    align-items: center;
  }
`;

const OriginalText = styled("div")`
  p,
  span {
    transition: all 0.5s ease;
  }

  &[data-highlight] {
    span,
    p {
      background-color: transparent;
    }

    p {
      color: rgba(0, 0, 0, ${fadedValue});
    }

    * > .long_sentence {
      color: rgb(183, 61, 117, ${fadedValue});
    }
    * > .grammatical_error {
      color: rgb(183, 117, 61, ${fadedValue});
    }
    * > .typo {
      color: rgb(61, 117, 183, ${fadedValue});
    }
  }

  &[data-highlight="long_sentence"] {
    * > .long_sentence {
      color: rgb(183, 61, 117);
      background-color: rgba(183, 61, 117, 0.1);
      & > span {
        color: rgb(183, 61, 117);
        background-color: transparent;
      }
    }
  }

  &[data-highlight="grammatical_error"] {
    * > .grammatical_error {
      color: rgb(183, 117, 61);
      background-color: rgba(183, 117, 61, 0.1);
    }
  }

  &[data-highlight="typo"] {
    * > .typo {
      color: rgb(61, 117, 183);
      background-color: rgba(61, 117, 183, 0.1);
    }
  }
`;

const ParsedTextContainer = styled("div")`
  margin-bottom: 1em;
`;
const MarkedElement = styled("span")<{ $index: string; $paragraphIndex: string }>`
  font-weight: bold;
  &:hover {
    cursor: pointer;
  }

  &.long_sentence {
    color: rgb(183, 61, 117);
    background-color: rgba(183, 61, 117, 0.1);
  }
  &.grammatical_error {
    color: rgb(183, 117, 61);
    background-color: rgba(183, 117, 61, 0.1);
  }
  &.typo {
    color: rgb(61, 117, 183);
    background-color: rgba(61, 117, 183, 0.1);
  }

  &[data-highlight-marker="long_sentence-${(props) => props.$paragraphIndex}-${(props) => props.$index}"] {
    color: rgb(183, 61, 117) !important;
    background-color: rgba(183, 61, 117, 0.1) !important;

    & > span {
      color: rgb(183, 61, 117) !important;
      background-color: transparent;
    }
  }

  &[data-highlight-marker="grammatical_error-${(props) => props.$paragraphIndex}-${(props) => props.$index}"] {
    color: rgb(183, 117, 61) !important;
    background-color: rgba(183, 117, 61, 0.1) !important;
  }

  &[data-highlight-marker="typo-${(props) => props.$paragraphIndex}-${(props) => props.$index}"] {
    color: rgb(61, 117, 183) !important;
    background-color: rgba(61, 117, 183, 0.1) !important;
  }
`;

const MarkersDefinitionsContainer = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 24px;
  align-items: center;
  flex-wrap: wrap;

  &[data-highlight] {
    > div {
      opacity: ${fadedValue};
    }
  }

  &[data-highlight="long_sentence"] {
    .long_sentence_definition {
      opacity: 1;
    }
  }

  &[data-highlight="grammatical_error"] {
    .grammatical_error_definition {
      opacity: 1;
    }
  }

  &[data-highlight="typo"] {
    .typo_definition {
      opacity: 1;
    }
  }

  > div {
    transition: opacity 0.5s ease;
  }
`;

const MarkerDefinition = styled("div")<{ $bgColour: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: auto;
  padding: 8px;
  border-radius: 10px;
  background-color: ${(props) => props.$bgColour};
  &:hover {
    cursor: pointer;
  }

  transition: opacity 0.5s ease;
`;

const MarkerCircle = styled("div")<{ $bgColour: string }>`
  display: block;
  height: 10px;
  width: 10px;
  border-radius: 100%;
  background-color: ${(props) => props.$bgColour};
`;

const FeedbackContainer = styled("div")`
  padding: 1em;
`;
const FeedbackListItem = styled("li")`
  display: list-item;
  span:nth-of-type(odd) {
    font-weight: bold;
    margin-top: 0.5em;
  }

  span {
    display: flex;
    align-items: center;
  }
`;

const LoadingContainer = styled("div")`
  @keyframes shake {
    0% {
      transform: translate(0, 0);
    }
    5% {
      transform: translate(-2px, 0);
    }
    10% {
      transform: translate(2px, 0);
    }
    15% {
      transform: translate(-2px, 0);
    }
    20% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(0, 0);
    }
  }
  @keyframes fadeInOut {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100px;
  div {
    position: relative;
    height: 100%;
    width: 100%;
    .question-mark {
      position: absolute;
      font-size: 1.5em;
      font-weight: bold;
      animation: fadeInOut 3s ease-in-out infinite;
    }
    .question-mark-1 {
      left: 0;
      top: 25px;
      animation-delay: 0s;
      rotate: -15deg;
    }
    .question-mark-2 {
      top: 0;
      left: 35px;
      animation-delay: 1s;
    }
    .question-mark-3 {
      top: 16px;
      left: 75px;
      animation-delay: 2s;
      rotate: 15deg;
    }
  }

  svg {
    animation: shake 5s ease-in-out infinite;
  }

  @keyframes float {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0);
    }
  }
`;

const BetaContainer = styled("span")`
  font-size: 0.75rem;
  font-weight: bold;
  margin-left: 8px;
  padding: 4px 8px;
  border: 2px solid ${Styles.Colors.THEME_PRIMARY};
  border-radius: 25px;
`;

function scrollToId(id: string) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

const AiFeedbackView: FC = () => {
  const { state, setGeneratedData } = useStore();
  const { t } = useTranslation("common");
  const [activeClass, setActiveClass] = useState<string | null>(null);
  const [activeClassClicked, setActiveClassClicked] = useState<string | null>(null);

  const hasFeedback = useMemo(() => {
    return (
      !!state.generatedData?.generatedResponse &&
      (state.generatedData.generatedResponse.feedback?.editors_note?.long_sentences?.length != 0 ||
        state.generatedData.generatedResponse.feedback?.editors_note?.grammatical_errors?.length != 0 ||
        state.generatedData.generatedResponse.feedback?.editors_note?.typos?.length != 0 ||
        state.generatedData.generatedResponse.feedback?.tone_adjustments?.length != 0 ||
        state.generatedData.generatedResponse.feedback?.questions_for_reflection?.length != 0)
    );
  }, [state.generatedData?.generatedResponse?.feedback]);

  function textParser(text: string, paragraphIndex: number): ReactNode {
    const markers = [
      { syntax: /\{\{(.*?)\}\}/, className: "long_sentence" },
      { syntax: /<<(.*?)>>/, className: "grammatical_error" },
      { syntax: /\[\[(.*?)\]\]/, className: "typo" },
    ];

    // Add a 'globalOffset' parameter to keep keys unique
    const parse = (input: string, globalOffset: number = 0): ReactNode[] => {
      if (!input) return [];

      let firstMatch: { index: number; match: RegExpExecArray; className: string } | null = null;

      for (const marker of markers) {
        // Create a new RegExp instance or reset lastIndex to avoid state issues
        const regex = new RegExp(marker.syntax, marker.syntax.flags);
        const match = regex.exec(input);

        if (match && (firstMatch === null || match.index < firstMatch.index)) {
          firstMatch = { index: match.index, match, className: marker.className };
        }
      }

      if (!firstMatch) return [input];

      const { index, match, className } = firstMatch;
      const before = input.slice(0, index);
      const innerText = match[1];
      const after = input.slice(index + match[0].length);

      // This unique ID is now stable because it uses the global position
      const uniqueId = `${className}-${paragraphIndex}-${globalOffset + index}`;

      return [
        before,
        <MarkedElement
          key={uniqueId}
          className={`${className} ${uniqueId}`}
          $index={(globalOffset + index).toString()}
          $paragraphIndex={paragraphIndex.toString()}
          data-highlight-marker={activeClass === uniqueId ? activeClass : undefined}
          onMouseEnter={() => setActiveClass(uniqueId)}
          onMouseLeave={() => setActiveClass(null)}
          onClick={(e) => {
            e.preventDefault();
            scrollToId(match[0]);
          }}
        >
          {/* Recurse for inner text - offset starts at the beginning of match */}
          {parse(innerText, globalOffset + index + match[0].indexOf(innerText))}
        </MarkedElement>,
        /* Recurse for after text - offset starts after the full match */
        ...parse(after, globalOffset + index + match[0].length),
      ];
    };

    return parse(text);
  }

  if (state.generatedData === null || state.generatedData.showGeneratedFeedback === false) {
    return null;
  }

  return (
    <FeedbackContainer>
      <TopRow>
        <h2>
          {t("components.aiFeedbackView.title")}
          <BetaContainer>BETA</BetaContainer>
        </h2>
        <Button
          id="schema-menu-button"
          variant="outlined"
          color="secondary"
          onClick={() => setGeneratedData({ ...state.generatedData, showGeneratedFeedback: false, doGenerate: false })}
          aria-label={t("components.aiFeedbackView.close")}
          title={t("components.aiFeedbackView.close")}
        >
          <Close />
        </Button>
      </TopRow>
      {state.generatedData?.loading ? (
        <div style={{ display: "flex", alignItems: "center", marginTop: "1em", flexDirection: "column" }}>
          <LoadingContainer>
            <div>
              <p className="question-mark question-mark-1">?</p>
              <p className="question-mark question-mark-2">?</p>
              <p className="question-mark question-mark-3">?</p>
            </div>
            <SmartToy style={{ marginRight: "0.5em" }} />
          </LoadingContainer>
          <span>{t("components.aiFeedbackView.generatingTextFeedback")}</span>
        </div>
      ) : !!state.generatedData?.generatedResponse ? (
        <div>
          {Array.isArray(state.generatedData.generatedResponse?.tags?.tagged_paragraphs) &&
            state.generatedData.generatedResponse?.tags?.tagged_paragraphs.length !== 0 && (
              <>
                <OriginalText data-highlight={activeClass || activeClassClicked}>
                  <h3>{t("components.aiFeedbackView.originalTextWithMarkings")}</h3>
                  <ParsedTextContainer>
                    {state.generatedData.generatedResponse.tags.tagged_paragraphs.map(
                      (paragraph: string, index: number) => (
                        <p key={index}>{textParser(paragraph, index)}</p>
                      )
                    )}
                  </ParsedTextContainer>
                </OriginalText>
                <MarkersDefinitionsContainer data-highlight={activeClass || activeClassClicked}>
                  {state.generatedData.generatedResponse.feedback?.editors_note?.long_sentences?.length != 0 && (
                    <MarkerDefinition
                      $bgColour="rgba(183, 61, 117, 0.1)"
                      className="long_sentence_definition"
                      onMouseEnter={() => setActiveClass("long_sentence")}
                      onMouseLeave={() => setActiveClass(null)}
                      onClick={() =>
                        setActiveClassClicked((prev) => (prev === "long_sentence" ? null : "long_sentence"))
                      }
                    >
                      <MarkerCircle $bgColour="rgb(183, 61, 117)" />
                      <span>{t("components.aiFeedbackView.longSentence")}</span>
                    </MarkerDefinition>
                  )}
                  {state.generatedData.generatedResponse.feedback?.editors_note?.grammatical_errors?.length != 0 && (
                    <MarkerDefinition
                      $bgColour="rgba(183, 117, 61, 0.1)"
                      className="grammatical_error_definition"
                      onMouseEnter={() => setActiveClass("grammatical_error")}
                      onMouseLeave={() => setActiveClass(null)}
                      onClick={() =>
                        setActiveClassClicked((prev) => (prev === "grammatical_error" ? null : "grammatical_error"))
                      }
                    >
                      <MarkerCircle $bgColour="rgb(183, 117, 61)" />
                      <span>{t("components.aiFeedbackView.grammaticalError")}</span>
                    </MarkerDefinition>
                  )}
                  {state.generatedData.generatedResponse.feedback?.editors_note?.typos?.length != 0 && (
                    <MarkerDefinition
                      $bgColour="rgba(61, 117, 183, 0.1)"
                      className="typo_definition"
                      onMouseEnter={() => setActiveClass("typo")}
                      onMouseLeave={() => setActiveClass(null)}
                      onClick={() => setActiveClassClicked((prev) => (prev === "typo" ? null : "typo"))}
                    >
                      <MarkerCircle $bgColour="rgb(61, 117, 183)" />
                      <span>{t("components.aiFeedbackView.typo")}</span>
                    </MarkerDefinition>
                  )}
                </MarkersDefinitionsContainer>
              </>
            )}
          <h3>{t("components.aiFeedbackView.feedback")}</h3>
          {!hasFeedback && <p>{t("components.aiFeedbackView.noFeedbackGenerated")}</p>}
          {Array.isArray(state.generatedData.generatedResponse.feedback?.["editors_note"]?.["long_sentences"]) &&
            state.generatedData.generatedResponse.feedback?.["editors_note"]?.["long_sentences"].length !== 0 && (
              <>
                <h4>{t("components.aiFeedbackView.sentenceStructure")}</h4>
                <ul>
                  {state.generatedData.generatedResponse.feedback?.["editors_note"]?.["long_sentences"].map(
                    (note: any, index: number) => (
                      <FeedbackListItem key={index} id={note?.quote}>
                        <span>{t("components.aiFeedbackView.quoteFromText")}</span>
                        <span>{note?.quote?.replace(/\{\{|\}\}|\[\[|\]\]|<<|>>/g, "")}</span>
                        <span>{t("components.aiFeedbackView.suggestionForImprovement")}</span>
                        <span>{note?.original_language_correction}</span>
                        <span>{t("components.aiFeedbackView.reasoning")}</span>
                        <span>{note?.reasoning}</span>
                      </FeedbackListItem>
                    )
                  )}
                </ul>
              </>
            )}
          {Array.isArray(state.generatedData.generatedResponse.feedback?.["editors_note"]?.["grammatical_errors"]) &&
            state.generatedData.generatedResponse.feedback?.["editors_note"]?.["grammatical_errors"].length !== 0 && (
              <>
                <h4>{t("components.aiFeedbackView.grammaticalErrors")}</h4>
                <ul>
                  {state.generatedData.generatedResponse.feedback?.["editors_note"]?.["grammatical_errors"].map(
                    (note: any, index: number) => (
                      <FeedbackListItem key={index} id={note?.quote}>
                        <span>{t("components.aiFeedbackView.quoteFromText")}</span>
                        <span>{note?.quote?.replace(/\{\{|\}\}|\[\[|\]\]|<<|>>/g, "")}</span>
                        <span>{t("components.aiFeedbackView.suggestionForImprovement")}</span>
                        <span>{note?.original_language_correction}</span>
                        <span>{t("components.aiFeedbackView.reasoning")}</span>
                        <span>{note?.reasoning}</span>
                      </FeedbackListItem>
                    )
                  )}
                </ul>
              </>
            )}
          {Array.isArray(state.generatedData.generatedResponse.feedback?.["editors_note"]?.["typos"]) &&
            state.generatedData.generatedResponse.feedback?.["editors_note"]?.["typos"].length !== 0 && (
              <>
                <h4>{t("components.aiFeedbackView.typos")}</h4>
                <ul>
                  {state.generatedData.generatedResponse.feedback?.["editors_note"]?.["typos"].map(
                    (note: any, index: number) => (
                      <FeedbackListItem key={index} id={note?.typo}>
                        <span>
                          {note?.typo?.replace(/\{\{|\}\}|\[\[|\]\]|<<|>>/g, "")} <ArrowRightAlt />{" "}
                          {note?.original_language_correction}
                        </span>
                        <span>{note?.reasoning}</span>
                      </FeedbackListItem>
                    )
                  )}
                </ul>
              </>
            )}
          {Array.isArray(state.generatedData.generatedResponse.feedback?.["editors_note"]?.["tone_adjustments"]) &&
            state.generatedData.generatedResponse.feedback?.["editors_note"]?.["tone_adjustments"].length !== 0 && (
              <>
                <h4>{t("components.aiFeedbackView.toneAdjustments")}</h4>
                <ul>
                  {state.generatedData.generatedResponse.feedback?.["editors_note"]?.["tone_adjustments"].map(
                    (note: string, index: number) => (
                      <li key={index}>{note}</li>
                    )
                  )}
                </ul>
              </>
            )}
          {Array.isArray(state.generatedData.generatedResponse.feedback?.["questions_for_reflection"]) &&
            state.generatedData.generatedResponse.feedback?.["questions_for_reflection"].length !== 0 && (
              <>
                <h4>{t("components.aiFeedbackView.questionsForReflection")}</h4>
                <ul>
                  {state.generatedData.generatedResponse.feedback?.["questions_for_reflection"].map(
                    (note: string, index: number) => (
                      <li key={index}>{note}</li>
                    )
                  )}
                </ul>
              </>
            )}
        </div>
      ) : null}
    </FeedbackContainer>
  );
};

export default AiFeedbackView;
